import { useState, useRef, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import type { POI } from '@poi-planner/shared';
import { FOCUS_RADIUS_KM } from '@poi-planner/shared';
import type { SortMode, TagWithCount, TagFilterMode, ActiveFilterCounts } from '../hooks/usePoiFilters';

export interface CollectionOption {
  id: string;
  name: string;
}

export interface TripOption {
  id: string;
  name: string;
}

type FilterCategory = 'tag' | 'trip' | 'collection' | 'source';

export interface PoiFilterBarProps {
  // From usePoiFilters
  search: string;
  sortMode: SortMode;
  sortAscending: boolean;
  tagFilters: Map<string, TagFilterMode>;
  allTags: TagWithCount[];
  onSearchChange: (value: string) => void;
  onSortModeChange: (mode: SortMode) => void;
  onSortAscendingChange: (ascending: boolean) => void;
  onToggleTag: (tag: string) => void;

  // Source filters (multi-select)
  sourceFilters?: Map<string, TagFilterMode>;
  allSourceTexts?: string[];
  onToggleSource?: (source: string) => void;

  // Collection filters (multi-select)
  collectionFilters?: Map<string, TagFilterMode>;
  collections?: CollectionOption[];
  onToggleCollection?: (collectionId: string) => void;

  // Trip filters (multi-select) - used in Library page
  tripFilters?: Map<string, TagFilterMode>;
  trips?: TripOption[];
  onToggleTrip?: (tripId: string) => void;

  // Clear all filters
  onClearAllFilters?: () => void;
  activeFilterCounts?: ActiveFilterCounts;

  // Trip filter toggle (Sidebar - filter to current trip only)
  tripFilter?: boolean;
  onTripFilterToggle?: (enabled: boolean) => void;
  tripName?: string;

  // Optional trip-specific (sidebar)
  hideScheduled?: boolean;
  onHideScheduledChange?: (value: boolean) => void;
  focusModeEnabled?: boolean;
  onFocusModeChange?: (value: boolean) => void;

  // Optional library-specific
  boundsFilterEnabled?: boolean;
  onBoundsFilterChange?: (value: boolean) => void;

  // Proximity target (both contexts)
  targetPoi?: POI | null;
  onClearTarget?: () => void;

  // POI counts for filter summary
  filteredCount?: number;
  totalCount?: number;
}

export function PoiFilterBar({
  search,
  sortMode,
  sortAscending,
  tagFilters,
  allTags,
  onSearchChange,
  onSortModeChange,
  onSortAscendingChange,
  onToggleTag,
  sourceFilters = new Map(),
  allSourceTexts = [],
  onToggleSource,
  collectionFilters = new Map(),
  collections = [],
  onToggleCollection,
  tripFilters = new Map(),
  trips = [],
  onToggleTrip,
  onClearAllFilters,
  activeFilterCounts,
  tripFilter,
  onTripFilterToggle,
  tripName,
  hideScheduled,
  onHideScheduledChange,
  focusModeEnabled,
  onFocusModeChange,
  boundsFilterEnabled,
  onBoundsFilterChange,
  targetPoi,
  onClearTarget,
  filteredCount,
  totalCount,
}: PoiFilterBarProps) {
  // Active filter category for the swappable cloud
  const [activeCategory, setActiveCategory] = useState<FilterCategory>('tag');

  // Expanded filter summary state
  const [summaryExpanded, setSummaryExpanded] = useState(false);

  // Cloud search state (for filtering within active category)
  const [cloudSearch, setCloudSearch] = useState('');
  const cloudSearchRef = useRef<HTMLInputElement>(null);

  // Clear cloud search when switching categories
  useEffect(() => {
    setCloudSearch('');
  }, [activeCategory]);

  // Determine if we should show the category selector (Library page has all 4)
  const hasMultipleCategories = onToggleTrip || onToggleCollection || onToggleSource;

  // Calculate filter counts if not provided
  const filterCounts = activeFilterCounts ?? {
    tag: tagFilters.size,
    trip: tripFilters.size,
    collection: collectionFilters.size,
    source: sourceFilters.size,
    total: tagFilters.size + tripFilters.size + collectionFilters.size + sourceFilters.size,
  };

  // Filter tags/items by cloud search
  const filteredTags = useMemo(() => {
    if (!cloudSearch.trim()) return allTags;
    const q = cloudSearch.toLowerCase();
    return allTags.filter((t) => t.tag.toLowerCase().includes(q));
  }, [allTags, cloudSearch]);

  const filteredTrips = useMemo(() => {
    if (!cloudSearch.trim()) return trips;
    const q = cloudSearch.toLowerCase();
    return trips.filter((t) => t.name.toLowerCase().includes(q));
  }, [trips, cloudSearch]);

  const filteredCollections = useMemo(() => {
    if (!cloudSearch.trim()) return collections;
    const q = cloudSearch.toLowerCase();
    return collections.filter((c) => c.name.toLowerCase().includes(q));
  }, [collections, cloudSearch]);

  const filteredSources = useMemo(() => {
    if (!cloudSearch.trim()) return allSourceTexts;
    const q = cloudSearch.toLowerCase();
    return allSourceTexts.filter((s) => s.toLowerCase().includes(q));
  }, [allSourceTexts, cloudSearch]);

  // Base pill classes
  const basePillClasses = 'inline-flex items-center px-2.5 py-1 text-[11px] font-medium rounded-full cursor-pointer transition-all duration-150';

  // Pill style helper
  const getPillClasses = (mode: TagFilterMode | undefined, baseColor: string, unselectedClasses: string) => {
    if (mode === 'exclude') {
      return 'bg-red-500 text-white shadow-[0_0_0_1px_rgba(239,68,68,0.5),0_1px_3px_rgba(0,0,0,0.2)]';
    }
    if (mode === 'include') {
      return `${baseColor} text-white shadow-[0_0_0_1px_rgba(0,0,0,0.1),0_1px_3px_rgba(0,0,0,0.2)]`;
    }
    return unselectedClasses;
  };

  const unselectedPillClasses = 'bg-white text-slate-500 shadow-[0_0_0_1px_rgba(0,0,0,0.06)] hover:shadow-[0_0_0_1px_rgba(0,0,0,0.1)] hover:bg-slate-50';

  // Build active filters list for summary
  const activeFilters = useMemo(() => {
    const filters: { key: string; label: string; category: FilterCategory; mode: TagFilterMode }[] = [];

    tagFilters.forEach((mode, tag) => {
      filters.push({ key: `tag-${tag}`, label: tag, category: 'tag', mode });
    });

    tripFilters.forEach((mode, tripId) => {
      const trip = trips.find((t) => t.id === tripId);
      if (trip) {
        filters.push({ key: `trip-${tripId}`, label: trip.name, category: 'trip', mode });
      }
    });

    collectionFilters.forEach((mode, collectionId) => {
      const collection = collections.find((c) => c.id === collectionId);
      if (collection) {
        filters.push({ key: `collection-${collectionId}`, label: collection.name, category: 'collection', mode });
      }
    });

    sourceFilters.forEach((mode, source) => {
      filters.push({ key: `source-${source}`, label: source, category: 'source', mode });
    });

    return filters;
  }, [tagFilters, tripFilters, collectionFilters, sourceFilters, trips, collections]);

  // Get color for category
  const getCategoryColor = (category: FilterCategory) => {
    switch (category) {
      case 'tag': return 'bg-slate-800';
      case 'trip': return 'bg-purple-600';
      case 'collection': return 'bg-amber-600';
      case 'source': return 'bg-slate-700';
    }
  };

  // Handle removing a filter from summary
  const handleRemoveFilter = (filter: { category: FilterCategory; label: string; key: string }) => {
    switch (filter.category) {
      case 'tag':
        onToggleTag(filter.label);
        break;
      case 'trip':
        if (onToggleTrip) {
          const tripId = filter.key.replace('trip-', '');
          onToggleTrip(tripId);
        }
        break;
      case 'collection':
        if (onToggleCollection) {
          const collectionId = filter.key.replace('collection-', '');
          onToggleCollection(collectionId);
        }
        break;
      case 'source':
        if (onToggleSource) {
          onToggleSource(filter.label);
        }
        break;
    }
  };

  // Category definitions
  const allCategories: { id: FilterCategory; label: string; count: number; available: boolean }[] = [
    { id: 'tag', label: 'Tag', count: filterCounts.tag, available: true },
    { id: 'trip', label: 'Trip', count: filterCounts.trip, available: !!onToggleTrip && trips.length > 0 },
    { id: 'collection', label: 'Collection', count: filterCounts.collection, available: !!onToggleCollection && collections.length > 0 },
    { id: 'source', label: 'Source', count: filterCounts.source, available: !!onToggleSource && allSourceTexts.length > 0 },
  ];
  const categories = allCategories.filter((c) => c.available);

  return (
    <>
      {/* Filter Summary - shown when filters are active and counts are provided */}
      {filteredCount !== undefined && totalCount !== undefined && (
        <div className="mb-2">
          <div
            className={clsx(
              'flex items-center justify-between px-2.5 py-1.5 rounded-lg transition-colors',
              filterCounts.total > 0 ? 'bg-slate-100 cursor-pointer hover:bg-slate-150' : 'bg-slate-50'
            )}
            onClick={() => filterCounts.total > 0 && setSummaryExpanded(!summaryExpanded)}
          >
            <div className="flex items-center gap-2 text-xs">
              <span className="font-semibold text-text-primary">
                {filteredCount === totalCount
                  ? `${totalCount} ${totalCount === 1 ? 'POI' : 'POIs'}`
                  : `${filteredCount} of ${totalCount} POIs`}
              </span>
              {filterCounts.total > 0 && (
                <>
                  <span className="text-text-muted">•</span>
                  <span className="text-text-secondary">
                    {filterCounts.total} {filterCounts.total === 1 ? 'filter' : 'filters'}
                  </span>
                  <span className="material-symbols-outlined text-sm text-text-muted">
                    {summaryExpanded ? 'expand_less' : 'expand_more'}
                  </span>
                </>
              )}
            </div>
            {filterCounts.total > 0 && onClearAllFilters && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClearAllFilters();
                  setSummaryExpanded(false);
                }}
                className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
              >
                Clear
                <span className="material-symbols-outlined text-xs">close</span>
              </button>
            )}
          </div>

          {/* Expanded filter details */}
          {summaryExpanded && filterCounts.total > 0 && (
            <div className="mt-1.5 px-2.5 py-2 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex flex-wrap gap-1.5">
                {activeFilters.map((filter) => (
                  <button
                    key={filter.key}
                    onClick={() => handleRemoveFilter(filter)}
                    title="Click to remove"
                    className={clsx(
                      'inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full transition-all',
                      filter.mode === 'exclude'
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : filter.category === 'tag'
                          ? 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                          : filter.category === 'trip'
                            ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                            : filter.category === 'collection'
                              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                              : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                    )}
                  >
                    {filter.mode === 'exclude' && (
                      <span className="material-symbols-outlined text-[10px]">remove</span>
                    )}
                    {filter.label}
                    <span className="material-symbols-outlined text-[10px]">close</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Category Selector - only show if we have multiple filter types */}
      {hasMultipleCategories && categories.length > 1 && (
        <div className="flex items-center gap-1.5 mb-2">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={clsx(
                'inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-full transition-all',
                activeCategory === cat.id
                  ? 'bg-slate-700 text-white shadow-sm'
                  : 'bg-transparent border border-slate-300 text-slate-500 hover:border-slate-400 hover:text-slate-600'
              )}
            >
              {cat.label}
              {cat.count > 0 && (
                <span
                  className={clsx(
                    'inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-bold rounded-full',
                    activeCategory === cat.id
                      ? 'bg-white/20 text-white'
                      : 'bg-blue-500 text-white'
                  )}
                >
                  {cat.count}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Cloud search - show if category has many items */}
      {((activeCategory === 'tag' && allTags.length > 8) ||
        (activeCategory === 'trip' && trips.length > 8) ||
        (activeCategory === 'collection' && collections.length > 8) ||
        (activeCategory === 'source' && allSourceTexts.length > 8)) && (
        <div className="relative mb-1.5">
          <span className="material-symbols-outlined absolute left-2 top-1.5 text-text-muted text-sm">
            search
          </span>
          <input
            ref={cloudSearchRef}
            type="text"
            placeholder={`Search ${activeCategory}s...`}
            value={cloudSearch}
            onChange={(e) => setCloudSearch(e.target.value)}
            className="w-full pl-7 pr-7 py-1 bg-slate-50 border-none rounded text-xs outline-none text-text-primary font-sans"
          />
          {cloudSearch && (
            <button
              onClick={() => setCloudSearch('')}
              className="absolute right-1 top-1 p-0.5 text-text-muted hover:text-text-primary transition-colors"
            >
              <span className="material-symbols-outlined text-sm">close</span>
            </button>
          )}
        </div>
      )}

      {/* Swappable Cloud Content */}
      <div className="flex items-start gap-1.5 flex-wrap max-h-[120px] overflow-y-auto custom-scrollbar">
        {/* Tag Cloud */}
        {activeCategory === 'tag' && filteredTags.map(({ tag, count }) => {
          const filterMode = tagFilters.get(tag);
          return (
            <button
              key={tag}
              onClick={() => onToggleTag(tag)}
              title={
                !filterMode
                  ? 'Click to include'
                  : filterMode === 'include'
                    ? 'Click to exclude'
                    : 'Click to clear'
              }
              className={clsx(
                basePillClasses,
                'gap-1',
                getPillClasses(filterMode, 'bg-slate-800', unselectedPillClasses)
              )}
            >
              <span>{tag}</span>
              {filterMode !== 'exclude' && (
                <span
                  className={clsx(
                    'tabular-nums text-[10px] font-semibold',
                    filterMode ? 'text-white/60' : 'text-slate-400'
                  )}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}

        {/* Trip Cloud */}
        {activeCategory === 'trip' && onToggleTrip && filteredTrips.map((trip) => {
          const filterMode = tripFilters.get(trip.id);
          return (
            <button
              key={trip.id}
              onClick={() => onToggleTrip(trip.id)}
              title={
                !filterMode
                  ? 'Click to include'
                  : filterMode === 'include'
                    ? 'Click to exclude'
                    : 'Click to clear'
              }
              className={clsx(
                basePillClasses,
                getPillClasses(filterMode, 'bg-purple-600', unselectedPillClasses)
              )}
            >
              {trip.name}
            </button>
          );
        })}

        {/* Collection Cloud */}
        {activeCategory === 'collection' && onToggleCollection && filteredCollections.map((collection) => {
          const filterMode = collectionFilters.get(collection.id);
          return (
            <button
              key={collection.id}
              onClick={() => onToggleCollection(collection.id)}
              title={
                !filterMode
                  ? 'Click to include'
                  : filterMode === 'include'
                    ? 'Click to exclude'
                    : 'Click to clear'
              }
              className={clsx(
                basePillClasses,
                getPillClasses(filterMode, 'bg-amber-600', unselectedPillClasses)
              )}
            >
              {collection.name}
            </button>
          );
        })}

        {/* Source Cloud */}
        {activeCategory === 'source' && onToggleSource && filteredSources.map((source) => {
          const filterMode = sourceFilters.get(source);
          return (
            <button
              key={source}
              onClick={() => onToggleSource(source)}
              title={
                !filterMode
                  ? 'Click to include'
                  : filterMode === 'include'
                    ? 'Click to exclude'
                    : 'Click to clear'
              }
              className={clsx(
                basePillClasses,
                getPillClasses(filterMode, 'bg-slate-700', unselectedPillClasses)
              )}
            >
              {source}
            </button>
          );
        })}

        {/* Empty states */}
        {activeCategory === 'tag' && filteredTags.length === 0 && (
          <span className="text-xs text-text-muted italic">
            {cloudSearch ? 'No matching tags' : 'No tags available'}
          </span>
        )}
        {activeCategory === 'trip' && filteredTrips.length === 0 && (
          <span className="text-xs text-text-muted italic">
            {cloudSearch ? 'No matching trips' : 'No trips available'}
          </span>
        )}
        {activeCategory === 'collection' && filteredCollections.length === 0 && (
          <span className="text-xs text-text-muted italic">
            {cloudSearch ? 'No matching collections' : 'No collections available'}
          </span>
        )}
        {activeCategory === 'source' && filteredSources.length === 0 && (
          <span className="text-xs text-text-muted italic">
            {cloudSearch ? 'No matching sources' : 'No sources available'}
          </span>
        )}
      </div>

      {/* Search */}
      <div className="relative mt-2 mb-2">
        <span className="material-symbols-outlined absolute left-3 top-2 text-text-secondary text-lg">
          search
        </span>
        <input
          type="text"
          placeholder="Search POIs..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full pl-10 pr-4 py-1.5 bg-input-bg border-none rounded-lg text-sm outline-none text-text-primary font-sans"
        />
      </div>

      {/* Sort + Icon toggle buttons - compact row */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {/* Target badge (replaces sort dropdown when active) */}
        {sortMode === 'distance' && targetPoi ? (
          <div className="flex items-center gap-1.5 px-2 py-1 text-[11px] font-semibold bg-blue-500/10 border border-blue-500/30 rounded-md text-blue-500">
            <span className="material-symbols-outlined text-sm">gps_fixed</span>
            <span className="max-w-[100px] overflow-hidden text-ellipsis whitespace-nowrap">
              {targetPoi.name}
            </span>
            <button
              onClick={() => onClearTarget?.()}
              className="flex items-center justify-center size-4 p-0 border-none rounded-full bg-blue-500/20 text-blue-500 cursor-pointer transition-colors hover:bg-blue-500/30"
            >
              <span className="material-symbols-outlined text-xs">close</span>
            </button>
          </div>
        ) : (
          <select
            value={sortMode}
            onChange={(e) => onSortModeChange(e.target.value as SortMode)}
            className="px-2 py-1 text-[11px] font-semibold bg-input-bg border-none rounded-md text-text-secondary cursor-pointer font-sans outline-none"
          >
            <option value="alpha">A → Z</option>
            <option value="timeBudget">Time</option>
            <option value="recentlyAdded">Recent</option>
          </select>
        )}

        {/* Sort direction */}
        <button
          onClick={() => onSortAscendingChange(!sortAscending)}
          title={sortAscending ? 'Sort descending' : 'Sort ascending'}
          className="flex items-center justify-center size-7 p-0 border-none rounded-md bg-input-bg text-text-secondary cursor-pointer transition-colors hover:bg-slate-200"
        >
          <span className="material-symbols-outlined text-base">
            {sortAscending ? 'arrow_upward' : 'arrow_downward'}
          </span>
        </button>

        {/* Hide scheduled toggle - icon only */}
        {onHideScheduledChange && (
          <button
            onClick={() => onHideScheduledChange(!hideScheduled)}
            title={hideScheduled ? 'Show all POIs' : 'Hide scheduled POIs'}
            className={clsx(
              'flex items-center justify-center size-7 p-0 rounded-md cursor-pointer transition-all',
              hideScheduled
                ? 'border border-text-primary bg-text-primary text-white'
                : 'border border-border bg-white text-text-secondary hover:bg-slate-50'
            )}
          >
            <span className="material-symbols-outlined text-base">
              {hideScheduled ? 'visibility_off' : 'visibility'}
            </span>
          </button>
        )}

        {/* Focus mode toggle - icon only */}
        {onFocusModeChange && (
          <button
            onClick={() => onFocusModeChange(!focusModeEnabled)}
            title={focusModeEnabled ? 'Show all POIs' : `Focus on POIs within ${FOCUS_RADIUS_KM}km`}
            className={clsx(
              'flex items-center justify-center size-7 p-0 rounded-md cursor-pointer transition-all',
              focusModeEnabled
                ? 'border border-emerald-600 bg-emerald-600 text-white'
                : 'border border-border bg-white text-text-secondary hover:bg-slate-50'
            )}
          >
            <span className="material-symbols-outlined text-base">
              {focusModeEnabled ? 'my_location' : 'location_searching'}
            </span>
          </button>
        )}

        {/* Bounds filter toggle - icon only */}
        {onBoundsFilterChange && (
          <button
            onClick={() => onBoundsFilterChange(!boundsFilterEnabled)}
            title={boundsFilterEnabled ? 'Show all POIs' : 'Filter to map view'}
            className={clsx(
              'flex items-center justify-center size-7 p-0 rounded-md cursor-pointer transition-all',
              boundsFilterEnabled
                ? 'border border-blue-600 bg-blue-600 text-white'
                : 'border border-border bg-white text-text-secondary hover:bg-slate-50'
            )}
          >
            <span className="material-symbols-outlined text-base">
              {boundsFilterEnabled ? 'crop_free' : 'frame_inspect'}
            </span>
          </button>
        )}

        {/* Trip filter toggle (Sidebar - current trip only) - icon only */}
        {onTripFilterToggle && (
          <button
            onClick={() => onTripFilterToggle(!tripFilter)}
            title={tripFilter ? 'Show all POIs' : `Show only POIs in ${tripName || 'this trip'}`}
            className={clsx(
              'flex items-center justify-center size-7 p-0 rounded-md cursor-pointer transition-all',
              tripFilter
                ? 'border border-purple-600 bg-purple-600 text-white'
                : 'border border-border bg-white text-text-secondary hover:bg-slate-50'
            )}
          >
            <span className="material-symbols-outlined text-base">
              {tripFilter ? 'luggage' : 'card_travel'}
            </span>
          </button>
        )}
      </div>
    </>
  );
}
